// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { RecaptchaScoreError } from 'recaptcha/RecaptchaScoreError'
import { RecaptchaTokenError } from 'recaptcha/RecaptchaTokenError'
import { RecaptchaVerificationError } from 'recaptcha/RecaptchaVerificationError'
import { RecaptchaVersion } from 'recaptcha/types'
import { useRecaptcha } from 'recaptcha/useRecaptcha'
import { isRecapthcaLowScoreError, isRecapthcaVerificationError } from 'recaptcha/utils'
import { Box, Button, Input } from '@node-space/ui-components'
import { RequestError } from 'components/Errors/RequestError'
import { HeaderCTA } from 'components/header/HeaderCTA'
import LoginPageLayout, { LoginPageLayoutVariant } from 'components/layout/LoginPageLayout'
import { PathNames } from 'constants/General'
import { useForgotPasswordMutation } from 'hooks/mutations/usePasswordMutation'
import { FingerprintFlows, useFingerprint } from 'hooks/useFingerprint'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { BaseErrorResponse } from 'types/beneficiaries'
import { isValidEmail } from 'utils/email'
import { logError, logSentryErrorAndGetTraceId } from 'utils/sentry'

export default function ForgotPassword() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [emailAddress, setEmailAddress] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [displayErrors, setDisplayErrors] = useState(false)
  const [initialView, setInitialView] = useState(true)
  const supportUrl = process.env.SUPPORT_URL

  const { mutate: forgotPassword, isPending } = useForgotPasswordMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { isLoadingRecaptcha, loadRecaptcha, getRecaptchaToken } = useRecaptcha('forgotPassword')
  const { getFingerprintEventId } = useFingerprint()

  useEffect(() => {
    loadRecaptcha()
  }, [loadRecaptcha])

  const getNewRecaptchaToken = async (): Promise<string> => {
    return getRecaptchaToken()
      .then(token => token)
      .catch(error => {
        logError('Error from ForgotPassword - getRecaptchaToken', error)
        return ''
      })
  }

  const getNewFingerprintEventId = async (): Promise<string> => {
    return getFingerprintEventId(FingerprintFlows.FORGOT_PASSWORD).catch(error => {
      logError('Error from ForgotPassword - getFingerprintEventId', error)
      return ''
    })
  }

  const submitRequest = async () => {
    resetRequestError()
    const [recaptchaToken, fingerprintEventId] = await Promise.all([
      getNewRecaptchaToken(),
      getNewFingerprintEventId(),
    ])

    if (!recaptchaToken) {
      setRequestError({ handledMessage: <RecaptchaTokenError />, show: true })
      return
    }

    forgotPassword(
      {
        emailAddress,
        recaptcha: recaptchaToken,
        recaptchaVersion: RecaptchaVersion.V3,
        fingerprintEventId,
      },
      {
        onSuccess: () => {
          setInitialView(false)
        },
        onError: (error: BaseErrorResponse) => {
          const errorResponseMessage = error?.data?.errorList?.[0]?.message

          if (isRecapthcaLowScoreError(errorResponseMessage)) {
            setRequestError({ handledMessage: <RecaptchaScoreError />, show: true })
            return
          }

          if (isRecapthcaVerificationError(errorResponseMessage)) {
            setRequestError({ handledMessage: <RecaptchaVerificationError />, show: true })
            return
          }

          const sentryTraceId = logSentryErrorAndGetTraceId(
            `Failed API request to /user/forgot/request with status ${error?.status} - useForgotPasswordMutation`,
            error,
            {
              data: { message: emailAddress },
            }
          )

          setRequestError({ show: true, errorCode: error?.status, sentryTraceId })
        },
      }
    )
  }
  const validateEmail = () => {
    if (isValidEmail(emailAddress)) {
      setEmailError(false)
    } else {
      setEmailError(true)
    }
  }

  useEffect(() => {
    validateEmail()
  }, [emailAddress])
  return (
    <LoginPageLayout
      title={initialView ? `${t('forgotPassword')}` : `${t('checkEmail')}`}
      description={
        initialView ? (
          'Just enter the email address you signed up with and we’ll send you a link to reset your password.'
        ) : (
          <>We sent an email to {emailAddress}</>
        )
      }
      headerRightContent={
        <HeaderCTA
          helpText={`${t('stillNeedHelp')}`}
          buttonText={`${t('contactSupport')}`}
          buttonUrl={supportUrl}
          externalLink
        />
      }
      variant={LoginPageLayoutVariant.NARROW}
    >
      <Box className="w-full space-y-6">
        {requestError?.show && (
          <RequestError
            errorCode={requestError?.errorCode}
            sentryTraceId={requestError?.sentryTraceId}
          >
            {requestError?.handledMessage}
          </RequestError>
        )}
        {initialView ? (
          <>
            <Input
              type="email"
              errorText="Invalid email address"
              error={emailError && displayErrors}
              inputMode="email"
              value={emailAddress}
              onChange={e => setEmailAddress(e.target.value)}
              placeholderText={`${t('yourEmailAddress')}`}
            />
            <div className="flex flex-col gap-y-1">
              <Button
                disabled={emailError && displayErrors}
                loading={isPending || isLoadingRecaptcha}
                full
                onClick={() => {
                  emailError ? setDisplayErrors(true) : submitRequest()
                }}
              >
                Send password reset link
              </Button>
              <Button variant="ghost" onClick={() => void navigate(PathNames.LOGIN)}>
                {t('backToLogin')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-center text-body-strong text-foreground-subdued">
              Sometimes these emails go missing, please check your spam folder if you don’t receive
              it soon.
            </p>
            <Button
              disabled={emailError && displayErrors}
              loading={isPending}
              full
              onClick={() => {
                void navigate(PathNames.LOGIN)
              }}
            >
              {`${t('backToLogin')}`}
            </Button>
          </>
        )}
      </Box>
    </LoginPageLayout>
  )
}
